import { useSyncExternalStore } from 'react';
import { readJson, writeStorage } from '@/lib/storage';

/**
 * Per-viewer display preferences, kept in this browser per user. Times show
 * in the tenant's zone unless the viewer picks UTC or their own zone.
 */
export type ZoneChoice = 'tenant' | 'utc' | 'local';
export type ClockChoice = 'locale' | '24h';

export interface Preferences {
  timeZone: ZoneChoice;
  clock: ClockChoice;
}

const DEFAULTS: Preferences = { timeZone: 'tenant', clock: 'locale' };
const cache = new Map<string, Preferences>();
const listeners = new Set<() => void>();

function key(userId: string): string {
  return `biller.prefs.${userId}`;
}

function isPreferences(value: unknown): value is Preferences {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    ['tenant', 'utc', 'local'].includes(String(v.timeZone)) && ['locale', '24h'].includes(String(v.clock))
  );
}

function read(userId: string): Preferences {
  let prefs = cache.get(userId);
  if (!prefs) {
    prefs = readJson('local', key(userId), isPreferences) ?? DEFAULTS;
    cache.set(userId, prefs);
  }
  return prefs;
}

export function setPreferences(userId: string, patch: Partial<Preferences>): void {
  const next = { ...read(userId), ...patch };
  cache.set(userId, next);
  writeStorage('local', key(userId), JSON.stringify(next));
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function usePreferences(userId: string): Preferences {
  return useSyncExternalStore(
    subscribe,
    () => read(userId),
    () => read(userId),
  );
}

/** The viewer's own zone, as an explicit choice (never applied implicitly). */
export function localTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

/** Preferences → the zone and clock instants render in. */
export function resolveDisplay(
  prefs: Preferences,
  tenantZone: string,
): { timeZone: string; hour12: boolean | undefined } {
  const timeZone =
    prefs.timeZone === 'utc' ? 'UTC' : prefs.timeZone === 'local' ? localTimeZone() : tenantZone;
  return { timeZone, hour12: prefs.clock === '24h' ? false : undefined };
}
